import { z } from 'zod';
import {
  appointmentStatuses,
  recurrenceScopes,
  type AppointmentKind,
  type AppointmentSource,
  type AppointmentStatus,
  type InvoiceStatus,
} from '../enums.js';
import type { AgendaNoteRuleRef } from './agenda-notes.js';
import { optionalText } from './_fields.js';

/**
 * Contrat réseau des rendez-vous, partagé mot pour mot entre le serveur et l'agenda.
 * Les durées sont en minutes et la fin est calculée par le serveur : deux sources pour la même
 * information (durée et heure de fin) finiraient par diverger, comme dans la V1.
 */

/**
 * Largeur maximale d'une fenêtre de lecture de l'agenda : deux mois affichés. Au-delà, une page
 * ne lit plus une plage, elle vide la table.
 *
 * **C'est la source unique.** Le serveur la fait respecter — liste des rendez-vous, liste des
 * absences — et le front taille ses fenêtres dessus. La valeur a d'abord été écrite des deux
 * côtés ; deux constantes pour une même règle finissent par diverger, et c'est l'utilisateur qui
 * découvre laquelle a gagné.
 */
export const MAX_WINDOW_DAYS = 62;

/**
 * Les trois marques qu'un rendez-vous peut porter. Un brouillon et une annulation n'en sont pas :
 * ils restent `null` sur le rendez-vous.
 */
export const appointmentBillingMarks = [
  'issued',
  'partially_paid',
  'paid',
] as const satisfies readonly InvoiceStatus[];
export type AppointmentBillingMark = (typeof appointmentBillingMarks)[number];

/* -------------------------------------------------------------------------- */
/* Entrées                                                                     */
/* -------------------------------------------------------------------------- */

export const appointmentIdParamSchema = z.object({ id: z.uuid() }).strict();

/**
 * La fenêtre est obligatoire : l'agenda lit toujours une plage visible, jamais « tout ».
 * Sa largeur maximale (`MAX_WINDOW_DAYS`) est vérifiée par le service.
 */
export const appointmentListQuerySchema = z
  .object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    practitionerMemberId: z.uuid().optional(),
    patientId: z.uuid().optional(),
    status: z.enum(appointmentStatuses).optional(),
  })
  .strict();
export type AppointmentListQuery = z.infer<typeof appointmentListQuerySchema>;

/** Statuts qu'une saisie manuelle peut poser d'emblée ; les autres passent par la machine à états. */
export const appointmentCreateStatuses = ['pending', 'confirmed'] as const;

export const appointmentCreateSchema = z
  .object({
    patientId: z.uuid().nullable().optional(),
    practitionerMemberId: z.uuid(),
    locationId: z.uuid().nullable().optional(),
    consultationTypeId: z.uuid().nullable().optional(),
    startsAt: z.iso.datetime(),
    /** Absente : la durée du type de consultation, sinon la durée par défaut du produit. */
    durationMinutes: z.int().min(5).max(600).optional(),
    status: z.enum(appointmentCreateStatuses).optional(),
    notes: optionalText(4000).optional(),
    privateNotes: optionalText(4000).optional(),
  })
  .strict();
export type AppointmentCreateInput = z.infer<typeof appointmentCreateSchema>;

export const appointmentUpdateSchema = z
  .object({
    patientId: z.uuid().nullable().optional(),
    practitionerMemberId: z.uuid().optional(),
    locationId: z.uuid().nullable().optional(),
    consultationTypeId: z.uuid().nullable().optional(),
    startsAt: z.iso.datetime().optional(),
    durationMinutes: z.int().min(5).max(600).optional(),
    notes: optionalText(4000).optional(),
    privateNotes: optionalText(4000).optional(),
    /**
     * Portée, quand le rendez-vous est l'occurrence d'une série. Absente ou `this` : cette
     * occurrence seule, qui se détache alors de la série. Sans série, la portée est ignorée —
     * un rendez-vous ordinaire n'a que lui-même.
     */
    scope: z.enum(recurrenceScopes).optional(),
  })
  .strict();
export type AppointmentUpdateInput = z.infer<typeof appointmentUpdateSchema>;

export const appointmentStatusChangeSchema = z
  .object({
    status: z.enum(appointmentStatuses),
    cancellationReason: optionalText(500).optional(),
  })
  .strict();
export type AppointmentStatusChangeInput = z.infer<typeof appointmentStatusChangeSchema>;

/**
 * Pourquoi une sortie que la machine à états permet reste fermée pour **ce** rendez-vous. Le
 * serveur refuse le changement avec le même code, l'écran affiche le bouton inerte et l'explique.
 *
 * - `external_read_only` : rendez-vous Doctena ou Docalia alors que la synchronisation
 *   bidirectionnelle du cabinet n'est pas active. L'annulation se fait chez le fournisseur.
 * - `provider_cancelled` : l'annulation n'a pas été posée dans OstéoPro (synchronisation,
 *   effacement RGPD) ; elle ne se rouvre pas à la main.
 */
export const appointmentStatusBlocks = ['external_read_only', 'provider_cancelled'] as const;
export type AppointmentStatusBlock = (typeof appointmentStatusBlocks)[number];

export interface AppointmentStatusOptionDto {
  status: AppointmentStatus;
  blockedBy: AppointmentStatusBlock | null;
}

/** Sorties de la machine à états depuis le statut courant, chacune avec son éventuel refus. */
export interface AppointmentStatusOptionsDto {
  options: AppointmentStatusOptionDto[];
}

/**
 * Vérification de conflit **avant** envoi d'un déplacement ou d'un redimensionnement : l'agenda
 * demande ce qu'un créneau heurterait, puis montre la réponse dans sa confirmation. Lecture pure.
 *
 * `excludeAppointmentId` est le rendez-vous qu'on déplace : sans lui, il se signalerait lui-même.
 */
export const appointmentConflictCheckSchema = z
  .object({
    practitionerMemberId: z.uuid(),
    startsAt: z.iso.datetime(),
    durationMinutes: z.int().min(5).max(600),
    excludeAppointmentId: z.uuid().optional(),
  })
  .strict();
export type AppointmentConflictCheckInput = z.infer<typeof appointmentConflictCheckSchema>;

/* -------------------------------------------------------------------------- */
/* Sorties                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Identité saisie lors d'une réservation publique, figée au moment de la prise de rendez-vous.
 * La V1 empilait ces valeurs dans une note libre, ce que l'audit a signalé comme un défaut RGPD.
 */
export interface AppointmentPatientSnapshot {
  firstName: string;
  lastName: string;
  phone?: string;
  email?: string;
  /** Réservation du widget : le dossier n'existe pas encore, la séance en aura besoin. */
  birthDate?: string;
  /**
   * Identifiant du patient chez l'agenda externe (Doctena). Il sert à rattacher, d'un coup, tous
   * les rendez-vous de cette personne quand le dossier du cabinet est créé.
   */
  externalPatientId?: string;
}

/**
 * Ce que l'agenda a besoin de savoir du dossier pour dessiner un événement et son dialogue.
 * Joint à la lecture du rendez-vous : sans lui, la page refaisait une requête par patient de la
 * fenêtre visible. Volontairement minimal — le dossier complet a sa propre route.
 */
export interface AppointmentPatientSummaryDto {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
}

export interface AppointmentDto {
  id: string;
  patientId: string | null;
  /**
   * Nul exactement quand `patientId` l'est : réservation publique pas encore rattachée à un
   * dossier, auquel cas `patientSnapshot` porte l'identité déclarée par le réservant.
   */
  patient: AppointmentPatientSummaryDto | null;
  patientSnapshot: AppointmentPatientSnapshot | null;
  practitionerMemberId: string;
  locationId: string | null;
  consultationTypeId: string | null;
  startsAt: string;
  endsAt: string;
  status: AppointmentStatus;
  source: AppointmentSource;
  /** `note` : post-it. Il n'ouvre pas de dossier, même s'il vient d'un rendez-vous Doctena. */
  kind: AppointmentKind;
  /** Libellé du post-it. Nul exactement quand `kind` vaut `appointment`. */
  noteTitle: string | null;
  /**
   * Règle de titre qui classe ce libellé, ou nulle. `active` faux : on a décidé de ne plus
   * classer les prochains imports, sans réécrire les notes déjà posées.
   */
  noteRule: AgendaNoteRuleRef | null;
  notes: string | null;
  privateNotes: string | null;
  cancellationReason: string | null;
  /**
   * « Conflit à régler » : le rendez-vous en chevauche un autre qui bloque — du même praticien,
   * ou de tout le cabinet quand l'offre n'a qu'un praticien. La saisie dans OstéoPro ne peut plus
   * en produire ; il vient d'un import Doctena ou Docalia, importé quand même et signalé
   * (décision du 30 septembre 2026), ou d'avant la règle. Calculé à la lecture de l'agenda : il
   * s'éteint de lui-même dès que l'un des deux est déplacé ou annulé. Faux hors de la liste.
   */
  hasConflict: boolean;
  /**
   * Marque de facturation de la séance, calculée à la lecture de la plage. Ce n'est pas la
   * facture : ni son numéro, ni son montant. `issued` : émise et encore due.
   * `partially_paid` : un paiement partiel. `paid` : soldée. `null` : brouillon, annulation,
   * avoir seul, rendez-vous sans consultation, post-it, ou toute lecture autre que la plage.
   * Plusieurs factures en vigueur : la plus encore due l'emporte.
   */
  billingMark: AppointmentBillingMark | null;
  /**
   * Série dont ce rendez-vous est une occurrence, nul pour un rendez-vous ordinaire. Le front
   * s'en sert pour badger l'occurrence et proposer une portée à la modification ou la
   * suppression.
   */
  seriesId: string | null;
  /** Rang dans la série, à partir de 1. Nul exactement quand `seriesId` l'est. */
  occurrenceIndex: number | null;
  /**
   * Renseigné dès que cette occurrence a été modifiée « pour cette fois seulement ». Elle garde
   * son rang, mais une action portant sur toute la série ne la touche plus.
   */
  seriesDetachedAt: string | null;
  /**
   * Instant d'envoi du courriel de rappel **pour l'horaire actuel**. Nul tant qu'aucun rappel
   * n'est parti, et de nouveau nul dès que le rendez-vous est déplacé : l'ancien rappel annonçait
   * une autre heure.
   */
  reminderSentAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Rendez-vous en conflit renvoyé avec l'erreur `appointment_overlap`, sans aucune donnée patient. */
export interface AppointmentConflictDto {
  id: string;
  practitionerMemberId: string;
  startsAt: string;
  endsAt: string;
}

/**
 * Rendez-vous chevauchant rendu par la vérification préalable. Il porte le même résumé de dossier
 * que la liste de l'agenda : la confirmation nomme le patient qu'on s'apprête à bousculer, alors
 * que le 409 d'une écriture refusée, lui, reste anonyme.
 */
export interface AppointmentOverlapDto {
  id: string;
  /** Un autre praticien que celui visé, quand l'offre interdit tout chevauchement au cabinet. */
  practitionerMemberId: string;
  startsAt: string;
  endsAt: string;
  patient: AppointmentPatientSummaryDto | null;
}

/**
 * Absence heurtée, projetée en instants : une exception est une date civile plus, éventuellement,
 * une tranche horaire ; le front ne refait pas ce calcul de fuseau. Une journée entière va de
 * minuit à minuit du lendemain, heure du cabinet.
 */
export interface AppointmentCheckAbsenceDto {
  id: string;
  startsAt: string;
  endsAt: string;
}

/**
 * Portée du refus de chevauchement : le même praticien, dans toutes les offres ; tout le cabinet
 * en Essentiel et Professionnel, où un seul praticien exerce à la fois.
 */
export const appointmentOverlapScopes = ['practitioner', 'cabinet'] as const;
export type AppointmentOverlapScope = (typeof appointmentOverlapScopes)[number];

export interface AppointmentConflictCheckDto {
  /**
   * Chevauchements **qui interdisent** l'écriture (depuis le 30 septembre 2026) : la
   * confirmation les montre, sans bouton pour passer outre.
   */
  overlaps: AppointmentOverlapDto[];
  overlapScope: AppointmentOverlapScope;
  absences: AppointmentCheckAbsenceDto[];
  /**
   * Vrai quand la plage demandée n'est pas entièrement couverte par les horaires hebdomadaires
   * **actifs** du praticien. Faux quand il n'en a aucun d'actif : pas d'horaires déclarés ne veut
   * pas dire hors horaires (docs/SCREENS.md, teintes de créneaux). Un renseignement, pas un
   * interdit : la création pendant une absence reste possible après confirmation.
   */
  outsideAvailability: boolean;
}
